import React, { useEffect, useState } from 'react';
import { Analytics } from '../../lib/api';
import { useApp } from '../../context/AppContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { TrendingUp, Users, FileCheck, Clock, Calendar, Tag, Building2, Loader2, Target } from 'lucide-react';
import { SOURCES } from '../../lib/prospectConfig';

const STATUS_COLORS = {
  potential: '#A78BFA',
  invited: '#94A3B8',
  confirmed: '#3B82F6',
  recorded: '#8B5CF6',
  edited: '#F59E0B',
  published: '#10B981'
};

const RELEASE_COLORS = {
  pending: '#94A3B8',
  sent: '#3B82F6',
  reminded: '#F59E0B',
  signed: '#10B981'
};

const SOURCE_COLORS = Object.fromEntries(SOURCES.map(s => [s.value, s.color]));
const SOURCE_LABELS = Object.fromEntries(SOURCES.map(s => [s.value, s.label]));

function Donut({ data, colors, total, labelMap }) {
  const entries = Object.entries(data).filter(([, v]) => v > 0);
  const sum = total ?? entries.reduce((a, [, v]) => a + v, 0);
  if (sum === 0) return <div className="text-stone-400 text-[12px] text-center py-6">No data</div>;
  let offset = 0;
  const radius = 56, circ = 2 * Math.PI * radius;
  return (
    <div className="flex items-center gap-5">
      <svg width={140} height={140} viewBox="0 0 140 140">
        <circle cx="70" cy="70" r={radius} fill="none" stroke="#F1EEE7" strokeWidth="16" />
        {entries.map(([k, v]) => {
          const frac = v / sum;
          const dash = `${frac * circ} ${circ - frac * circ}`;
          const seg = (
            <circle key={k} cx="70" cy="70" r={radius} fill="none"
              stroke={colors[k] || '#999'} strokeWidth="16"
              strokeDasharray={dash} strokeDashoffset={-offset}
              transform="rotate(-90 70 70)" strokeLinecap="butt" />
          );
          offset += frac * circ;
          return seg;
        })}
        <text x="70" y="66" textAnchor="middle" className="fill-stone-900" style={{ fontSize: 22, fontFamily: 'Playfair Display, serif' }}>{sum}</text>
        <text x="70" y="86" textAnchor="middle" className="fill-stone-500" style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 1 }}>total</text>
      </svg>
      <div className="space-y-1.5 flex-1">
        {entries.map(([k, v]) => (
          <div key={k} className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-sm" style={{ background: colors[k] || '#999' }} />
            <span className="text-[12.5px] text-stone-700 capitalize flex-1">{(labelMap && labelMap[k]) || k}</span>
            <span className="text-[12.5px] font-medium text-stone-900">{v}</span>
            <span className="text-[11px] text-stone-400 w-9 text-right">{Math.round(v / sum * 100)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Bars({ items, accent, valueKey = 'count', labelKey = 'month', max }) {
  const m = max ?? Math.max(1, ...items.map(i => i[valueKey] || 0));
  if (items.length === 0) return <div className="text-stone-400 text-[12px] text-center py-6">No data</div>;
  return (
    <div className="space-y-2">
      {items.map((it, i) => (
        <div key={`${labelKey}-${it[labelKey] ?? i}`} className="flex items-center gap-2">
          <div className="text-[11.5px] text-stone-600 w-20 truncate">{it[labelKey]}</div>
          <div className="flex-1 bg-stone-100 rounded-full h-3 overflow-hidden">
            <div className="h-full rounded-full transition-all" style={{ width: `${(it[valueKey] / m) * 100}%`, background: accent }} />
          </div>
          <div className="text-[12px] font-medium text-stone-900 w-10 text-right">{it[valueKey]}</div>
        </div>
      ))}
    </div>
  );
}

function Stat({ icon: Icon, label, value, sub, accent }) {
  return (
    <Card className="border-stone-200">
      <CardContent className="p-4 flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg grid place-items-center" style={{ background: `${accent}1f`, color: accent }}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold">{label}</div>
          <div className="text-2xl font-serif text-stone-900">{value}</div>
          {sub && <div className="text-[11px] text-stone-500">{sub}</div>}
        </div>
      </CardContent>
    </Card>
  );
}

export default function AnalyticsView() {
  const { activeShowId, activeShow } = useApp();
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    Analytics.get(activeShowId).then(setData);
  }, [activeShowId]);

  if (!data) return <div className="h-full grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-stone-400" /></div>;

  const monthMax = Math.max(1, ...data.byMonth.map(b => b.count));

  return (
    <div className="p-5 overflow-auto h-full">
      <div className="mb-5">
        <h1 className="text-[24px] font-serif text-stone-900">Analytics</h1>
        <p className="text-[12.5px] text-stone-500">{activeShow.name}</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Stat icon={Users} label="Total guests" value={data.total} accent={activeShow.color} />
        <Stat icon={FileCheck} label="Released" value={data.releaseCounts.signed || 0} sub={`${Math.round(((data.releaseCounts.signed || 0) / Math.max(1, data.total)) * 100)}% of total`} accent="#10B981" />
        <Stat icon={Clock} label="Avg days to sign" value={data.avgDaysToSign != null ? data.avgDaysToSign : '—'} sub={data.signedSampleSize ? `n=${data.signedSampleSize}` : 'no signed yet'} accent="#3B82F6" />
        <Stat icon={TrendingUp} label="Published episodes" value={data.statusCounts.published || 0} accent="#8B5CF6" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="border-stone-200">
          <CardHeader className="pb-2"><CardTitle className="text-[14px]">Episode status</CardTitle></CardHeader>
          <CardContent><Donut data={data.statusCounts} colors={STATUS_COLORS} /></CardContent>
        </Card>
        <Card className="border-stone-200">
          <CardHeader className="pb-2"><CardTitle className="text-[14px]">Release status</CardTitle></CardHeader>
          <CardContent><Donut data={data.releaseCounts} colors={RELEASE_COLORS} /></CardContent>
        </Card>
        <Card className="border-stone-200">
          <CardHeader className="pb-2"><CardTitle className="text-[14px] flex items-center gap-2"><Calendar className="w-3.5 h-3.5" />Episodes per month</CardTitle></CardHeader>
          <CardContent><Bars items={data.byMonth} accent={activeShow.color} max={monthMax} /></CardContent>
        </Card>
        <Card className="border-stone-200">
          <CardHeader className="pb-2"><CardTitle className="text-[14px]">Platform coverage</CardTitle></CardHeader>
          <CardContent>
            <Bars
              items={[
                { label: 'Apple Podcasts', count: data.platformCounts.apple },
                { label: 'Spotify', count: data.platformCounts.spotify },
                { label: 'YouTube', count: data.platformCounts.youtube },
                { label: 'Libsyn / Other', count: data.platformCounts.libsyn }
              ]}
              accent={activeShow.accent || '#C9A961'}
              labelKey="label"
              max={data.total}
            />
          </CardContent>
        </Card>
        <Card className="border-stone-200">
          <CardHeader className="pb-2"><CardTitle className="text-[14px] flex items-center gap-2"><Building2 className="w-3.5 h-3.5" />Top companies</CardTitle></CardHeader>
          <CardContent>
            {data.topCompanies.length === 0
              ? <div className="text-stone-400 text-[12px] text-center py-6">No data</div>
              : <Bars items={data.topCompanies} accent="#3B82F6" labelKey="name" />}
          </CardContent>
        </Card>
        <Card className="border-stone-200">
          <CardHeader className="pb-2"><CardTitle className="text-[14px] flex items-center gap-2"><Tag className="w-3.5 h-3.5" />Top tags</CardTitle></CardHeader>
          <CardContent>
            {data.topTags.length === 0
              ? <div className="text-stone-400 text-[12px] text-center py-6">No tagged guests</div>
              : <Bars items={data.topTags} accent="#10B981" labelKey="name" />}
          </CardContent>
        </Card>

        <Card className="border-stone-200" data-testid="booked-by-source-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-[14px] flex items-center gap-2">
              <Target className="w-3.5 h-3.5" /> Source of bookings
              <span className="text-[10.5px] text-stone-400 font-normal ml-auto">what actually converts</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Donut
              data={Object.fromEntries((data.bookedBySource || []).map(s => [s.source, s.count]))}
              colors={SOURCE_COLORS}
              labelMap={SOURCE_LABELS}
            />
          </CardContent>
        </Card>

        <Card className="border-stone-200" data-testid="prospects-by-source-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-[14px] flex items-center gap-2">
              <Users className="w-3.5 h-3.5" /> Prospect pipeline by source
              <span className="text-[10.5px] text-stone-400 font-normal ml-auto">where leads come from</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Donut
              data={Object.fromEntries((data.prospectsBySource || []).map(s => [s.source, s.count]))}
              colors={SOURCE_COLORS}
              labelMap={SOURCE_LABELS}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
